import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { RandomLocalArea } from "@/components/RandomLocalArea";
import {
  Check, ChevronLeft, ChevronRight, Loader2, Home, Upload, FileText,
  ClipboardCheck, CreditCard, X, Search, Building2, TrendingDown,
} from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const steps = ["Lead Type", "Data Source", "Review & Pay"];

function countRows(text) {
  return Math.max(0, text.split(/\r?\n/).filter((l) => l.trim() !== "").length - 1);
}

export default function NewRun() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [step, setStep] = useState(0);
  const [cfg, setCfg] = useState(null);
  const [type, setType] = useState("");
  const [source, setSource] = useState("attom"); // attom | csv
  const [zips, setZips] = useState("");
  const [propType, setPropType] = useState("");
  const [statewide, setStatewide] = useState(false);
  const [csv, setCsv] = useState("");
  const [fileName, setFileName] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [paying, setPaying] = useState(false);
  const [zipClaims, setZipClaims] = useState({});

  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)); }, []);
  useEffect(() => { if (type === "Purchase") setSource("listings"); }, [type]);
  const purchaseOnly = type === "Purchase";

  useEffect(() => {
    if (source !== "attom" && source !== "listings") { setZipClaims({}); return; }
    const list = zips.split(",").map((z) => z.trim()).filter(Boolean);
    if (!list.length) { setZipClaims({}); return; }
    const t = setTimeout(() => {
      api.get(`/runs/zip-claims?zips=${encodeURIComponent(list.join(","))}`)
        .then((r) => setZipClaims(r.data || {})).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [zips, source]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) { toast.error("Please upload a .csv file"); return; }
    const reader = new FileReader();
    reader.onload = () => { const t = String(reader.result || ""); setCsv(t); setFileName(file.name); setRowCount(countRows(t)); };
    reader.readAsText(file);
  };

  const zipList = () => zips.split(",").map((z) => z.trim()).filter(Boolean);
  const canNext = () => (step === 0 ? !!type : step === 1 ? (source === "csv" ? rowCount > 0 : statewide || zipList().length > 0) : true);

  const startPayment = async () => {
    setPaying(true);
    try {
      const payload = { prospect_type: type, source };
      if (source === "attom" || source === "listings") payload.attom_query = statewide ? { statewide: "FL", property_type: propType } : { zips: zipList(), property_type: propType };
      else payload.csv_data = csv;
      const run = await api.post("/runs", payload);
      if (isAdmin) {
        await api.post(`/runs/${run.data.id}/waive`);
        toast.success("Run started — cost waived (admin)");
        nav("/prospects");
        return;
      }
      const { data } = await api.post("/payments/checkout", { run_id: run.data.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setPaying(false); }
  };

  if (!cfg) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const perRun = cfg.prospects_per_run;

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader title="New Prospecting Run" subtitle={`${money(cfg.price)} per run · AI enriches & scores up to ${perRun} leads`} />

      {cfg.market_rate && (
        <div className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-900" data-testid="market-rate-banner">
          <TrendingDown className="h-4 w-4 text-emerald-600" />
          <span>Today's 30-yr market rate:</span>
          <b data-testid="market-rate-value">{Number(cfg.market_rate.rate).toFixed(2)}%</b>
          {cfg.market_rate.date && <span className="text-emerald-700/70">(week ending {cfg.market_rate.date})</span>}
          <span className="text-emerald-700/80">· Refinance leads are only sourced when a homeowner's inferred rate is above this.</span>
        </div>
      )}


      <div className="mb-8 flex items-center">
        {steps.map((s, i) => (
          <div key={s} className="flex flex-1 items-center last:flex-none">
            <div className="flex items-center gap-2">
              <div className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold ${i < step ? "bg-emerald-500 text-white" : i === step ? "gold-cta" : "bg-slate-200 text-slate-500"}`}>
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`hidden sm:block text-sm font-medium ${i <= step ? "text-slate-900" : "text-slate-400"}`}>{s}</span>
            </div>
            {i < steps.length - 1 && <div className={`mx-2 h-0.5 flex-1 ${i < step ? "bg-emerald-500" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      <Card className="p-6 sm:p-8">
        {step === 0 && (
          <div>
            <div className="mb-4 flex items-center gap-2 text-slate-900"><Home className="h-5 w-5 text-amber-600" /><h3 className="font-display text-lg font-bold">Select Lead Type</h3></div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {cfg.opportunity_types.map((t) => (
                <button key={t} type="button" data-testid={`type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setType(t)}
                  className={`rounded-xl border-2 p-4 text-left transition-all ${type === t ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">{t}</span>{type === t && <Check className="h-5 w-5 text-amber-600" />}</div>
                  {t === "All Mortgage Opportunities" && <p className="mt-1 text-xs text-slate-500">AI classifies each record into its strongest opportunity.</p>}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            {purchaseOnly && <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="purchase-listings-note">Purchase leads are only sourced from homes currently for sale, so Active Listings is the only source for this lead type.</div>}
            <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button onClick={() => setSource("attom")} disabled={purchaseOnly} data-testid="source-attom" className={`rounded-xl border-2 p-3 text-left disabled:cursor-not-allowed disabled:opacity-40 ${source === "attom" ? "border-amber-500 bg-amber-50" : "border-slate-200"}`}>
                <div className="flex items-center gap-2 font-semibold text-slate-900"><Building2 className="h-4 w-4 text-amber-600" /> ATTOM + <Home className="h-4 w-4 text-amber-600" /> Homes for sale</div>
                <p className="mt-1 text-xs text-slate-500">Both searches run together: owner, mortgage & equity data plus active listings</p>
              </button>
              {purchaseOnly && (
                <button onClick={() => setSource("listings")} data-testid="source-listings" className={`rounded-xl border-2 p-3 text-left ${source === "listings" ? "border-amber-500 bg-amber-50" : "border-slate-200"}`}>
                  <div className="flex items-center gap-2 font-semibold text-slate-900"><Home className="h-4 w-4 text-amber-600" /> Homes for sale</div>
                  <p className="mt-1 text-xs text-slate-500">Active listings — sellers who likely need a new purchase loan</p>
                </button>
              )}
              <button onClick={() => setSource("csv")} disabled={purchaseOnly} data-testid="source-csv" className={`rounded-xl border-2 p-3 text-left disabled:cursor-not-allowed disabled:opacity-40 ${source === "csv" ? "border-amber-500 bg-amber-50" : "border-slate-200"}`}>
                <div className="flex items-center gap-2 font-semibold text-slate-900"><Upload className="h-4 w-4 text-amber-600" /> Upload CSV</div>
                <p className="mt-1 text-xs text-slate-500">Use a list you already have</p>
              </button>
            </div>

            {source !== "csv" ? (
              <div className="space-y-4">
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-3" data-testid="statewide-fl">
                  <Switch checked={statewide} onCheckedChange={setStatewide} data-testid="statewide-fl-switch" />
                  <span className="text-sm"><span className="font-semibold text-slate-900">Search the entire state of Florida</span>
                    <span className="block text-xs text-slate-500">Searches ZIP codes across Florida in random order until your run has enough leads.</span></span>
                </label>
                {!statewide && <div>
                  <Label>ZIP code(s) <span className="text-xs font-normal text-slate-400">(comma-separated)</span></Label>
                  <div className="relative mt-1.5">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input className="pl-9" value={zips} onChange={(e) => setZips(e.target.value)} placeholder="32714, 32701" data-testid="attom-zips" />
                  </div>
                  <RandomLocalArea onPick={setZips} />
                  {Object.values(zipClaims).some((n) => n > 0) && (
                    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="zip-claims-note">
                      Heads up — some ZIPs already have team-claimed leads (you'll still receive fresh, un-duplicated ones): {Object.entries(zipClaims).filter(([, n]) => n > 0).map(([z, n]) => `${z}: ${n} taken`).join(" · ")}
                    </div>
                  )}
                </div>}
                <div>
                  <Label>Property type <span className="text-xs font-normal text-slate-400">(optional)</span></Label>
                  <Input className="mt-1.5" value={propType} onChange={(e) => setPropType(e.target.value)} placeholder='e.g. Single Family Dwelling' data-testid="attom-proptype" />
                </div>
                <p className="text-xs text-slate-400">ATTOM returns owner name, occupancy, mortgage balance and loan origination date — so equity, LTV and the inferred existing rate are populated. Phone &amp; email are appended automatically via AvaData when available; unknown fields stay empty (never fabricated).</p>
              </div>
            ) : !csv ? (
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-10 text-center hover:border-amber-400 hover:bg-amber-50/40" data-testid="csv-dropzone">
                <Upload className="mb-2 h-8 w-8 text-slate-400" />
                <span className="font-semibold text-slate-700">Click to upload a .csv file</span>
                <span className="mt-1 text-xs text-slate-400">Columns: name, address, city, state, zip, email, phone, property_value, mortgage_balance, origination_date, owner_occupied, age</span>
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="csv-input" />
              </label>
            ) : (
              <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <div className="flex items-center gap-3"><FileText className="h-6 w-6 text-emerald-600" />
                  <div><div className="font-semibold text-slate-900">{fileName}</div><div className="text-xs text-slate-500">{rowCount} record{rowCount === 1 ? "" : "s"} · up to {perRun} processed</div></div>
                </div>
                <button onClick={() => { setCsv(""); setFileName(""); setRowCount(0); }} className="text-slate-400 hover:text-red-500" data-testid="csv-remove"><X className="h-5 w-5" /></button>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="mb-4 flex items-center gap-2 text-slate-900"><ClipboardCheck className="h-5 w-5 text-amber-600" /><h3 className="font-display text-lg font-bold">Review &amp; Pay</h3></div>
            <div className="space-y-4">
              <Review label="Lead Type" value={type} />
              <Review label="Source" value={source === "attom" ? "ATTOM property search + homes for sale" : source === "listings" ? "Homes for sale (active listings)" : "CSV upload"} />
              {source !== "csv"
                ? <Review label="Search" value={`${statewide ? "Entire state of Florida" : "ZIP " + zipList().join(", ")}${propType ? " · " + propType : ""}`} />
                : <Review label="File" value={`${fileName || "—"} (${rowCount} records)`} />}
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
                <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">Prospecting Run</span><span className="font-display text-2xl font-extrabold text-slate-900">{money(cfg.price)}</span></div>
                <p className="mt-1 text-xs text-slate-500">{isAdmin ? "As an administrator, this run is free — the cost is waived." : `After secure payment, AI enriches, classifies and scores up to ${perRun} leads — then you review before any outreach.`}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="wizard-back"><ChevronLeft className="mr-1 h-4 w-4" /> Back</Button>
          {step < steps.length - 1 ? (
            <Button disabled={!canNext()} onClick={() => setStep((s) => s + 1)} data-testid="wizard-next" className="bg-slate-900 hover:bg-slate-800">Continue <ChevronRight className="ml-1 h-4 w-4" /></Button>
          ) : (
            <Button disabled={paying} onClick={startPayment} data-testid="pay-btn" className="gold-cta border-0 font-bold h-11 px-6">
              {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : isAdmin ? <><Check className="mr-2 h-4 w-4" /> Start Free Run (waived)</> : <><CreditCard className="mr-2 h-4 w-4" /> Pay {money(cfg.price)} &amp; Generate</>}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

function Review({ label, value }) {
  return (
    <div className="flex flex-col gap-1 border-b border-slate-100 pb-3 sm:flex-row sm:justify-between">
      <span className="text-sm font-semibold text-slate-500">{label}</span>
      <span className="text-sm text-slate-900 sm:text-right sm:max-w-md break-words">{value}</span>
    </div>
  );
}
